import { Button, CloseIcon, JoonUIColor, Typography } from "@joonapp/web-shared"
import { useEffect, useRef, useState } from "react"
import AudioAnalyser from "react-audio-analyser"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useSendMailModalStore } from "./hooks/useSendMailModalStore"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import { formatSecondsToTimer } from "../../../../util/util"

enum AudioStatus {
  INACTIVE = "inactive",
  RECORDING = "recording",
  PAUSED = "paused",
}

interface MailAudioModalStoreProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export const useMailAudioModalStore = create<MailAudioModalStoreProps>()(
  (set) => ({
    isOpen: false,
    onOpen: () => set({ isOpen: true }),
    onClose: () => set({ isOpen: false }),
  })
)

const MailAudioModal = () => {
  const { setAudioPreviewUrl, setAudioFile } = useSendMailModalStore()
  const { isOpen, onClose } = useMailAudioModalStore()
  const { t } = useTranslation()

  const [stream, setStream] = useState<MediaStream | null>(null)
  const [status, setStatus] = useState(AudioStatus.INACTIVE)
  const [recordingTime, setRecordingTime] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const onCloseModal = () => {
    setStatus(AudioStatus.INACTIVE)
    setAudioPreviewUrl(null)
    setAudioFile(null)
    setStream(null)
    setRecordingTime(0)
    if (timerRef.current) clearInterval(timerRef.current)
    if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
    onClose()
  }

  const audioProps = {
    audioType: "audio/wav",
    status,
    timeslice: 1000,
    startCallback: (e: any) => {
      const target = e.currentTarget as MediaRecorder
      setStatus(AudioStatus.RECORDING)
      if (e && target.stream) setStream(target.stream)
      setRecordingTime(0)
      if (timerRef.current) clearInterval(timerRef.current)
      timerRef.current = setInterval(() => setRecordingTime((t) => t + 1), 1000)
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
      pauseTimeoutRef.current = setTimeout(() => {
        audioProps.pauseCallback?.()
      }, 30000) // max recording time is 30 seconds
    },
    pauseCallback: () => {
      setStatus(AudioStatus.PAUSED)
      if (timerRef.current) clearInterval(timerRef.current)
    },
    stopCallback: (blob: Blob) => {
      setStatus(AudioStatus.INACTIVE)
      setStream(null)
      if (timerRef.current) clearInterval(timerRef.current)
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
      setRecordingTime(0)
      const file = new File([blob], "audio.wav", { type: "audio/wav" })
      setAudioFile(file)
      setAudioPreviewUrl(window.URL.createObjectURL(file))
      onClose()
    },
  }

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current)
    }
  }, [])

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onCloseModal}
      size="fit-content"
      backgroundType="overlay"
      zIndex={1100}
    >
      <button className="absolute right-4 top-4 p-2" onClick={onClose}>
        <CloseIcon color={JoonUIColor.icon.neutral} />
      </button>
      <div className="flex flex-col gap-2 items-center p-4 w-full">
        <Typography variant="bodyBold" textAlign="left">
          {t("MAIL_RECORD_CLIP")}
        </Typography>
        <div className="flex flex-col items-start mt-2 w-full">
          <div className="flex gap-2 items-center w-full">
            <div
              className="border border-borderPrimary rounded bg-white flex items-center justify-start relative w-full"
              style={{ height: 50, minHeight: 50 }}
            >
              {(status === AudioStatus.RECORDING ||
                status === AudioStatus.PAUSED) &&
                stream && (
                  <LiveWaveform
                    stream={stream}
                    isActive={status === AudioStatus.RECORDING}
                    className="w-[calc(100%-50px)] h-full"
                  />
                )}
              <Typography
                variant="bodySmallBold"
                className="absolute right-1 top-1/2 text-xs text-gray-500 bg-white bg-opacity-80 px-1 rounded"
                style={{
                  transform: "translateY(-50%)",
                  pointerEvents: "none",
                  minWidth: 36,
                  textAlign: "right",
                }}
              >
                {status === AudioStatus.RECORDING ||
                status === AudioStatus.PAUSED
                  ? formatSecondsToTimer(recordingTime)
                  : "0:00"}
              </Typography>
            </div>
            <div style={{ display: "none" }}>
              <AudioAnalyser {...audioProps} />
            </div>
          </div>
        </div>

        <Button
          text={
            status === AudioStatus.RECORDING || status === AudioStatus.PAUSED
              ? t("QUEST_SAVE")
              : t("MAIL_START_RECORDING")
          }
          onClick={() => {
            if (
              status === AudioStatus.RECORDING ||
              status === AudioStatus.PAUSED
            ) {
              setStatus(AudioStatus.INACTIVE)
            } else {
              setStatus(AudioStatus.RECORDING)
            }
          }}
          fullWidth
        />
      </div>
    </SlideUpModal>
  )
}

type LiveWaveformProps = {
  stream: MediaStream
  isActive: boolean
  className?: string
}
const LiveWaveform = ({ stream, isActive, className }: LiveWaveformProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const animationRef = useRef<number>()
  const analyserRef = useRef<AnalyserNode | null>(null)
  const dataArrayRef = useRef<Uint8Array | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)

  useEffect(() => {
    if (!stream || !isActive) return

    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext
    audioContextRef.current = new AudioCtx()
    sourceRef.current = audioContextRef.current.createMediaStreamSource(stream)
    analyserRef.current = audioContextRef.current.createAnalyser()
    sourceRef.current.connect(analyserRef.current)
    analyserRef.current.fftSize = 2048
    const bufferLength = analyserRef.current.fftSize
    dataArrayRef.current = new Uint8Array(bufferLength)

    const draw = () => {
      const canvas = canvasRef.current
      const analyser = analyserRef.current
      const dataArray = dataArrayRef.current
      if (!canvas || !analyser || !dataArray) return
      const ctx = canvas.getContext("2d")
      if (!ctx) return
      analyser.getByteTimeDomainData(dataArray)

      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.beginPath()
      const sliceWidth = canvas.width / bufferLength
      let x = 0
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0
        const y = (v * canvas.height) / 2
        if (i === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
        x += sliceWidth
      }
      ctx.lineTo(canvas.width, canvas.height / 2)
      ctx.strokeStyle = "#4F46E5"
      ctx.lineWidth = 2
      ctx.stroke()
      animationRef.current = requestAnimationFrame(draw)
    }

    draw()

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current)
      if (analyserRef.current) analyserRef.current.disconnect()
      if (sourceRef.current) sourceRef.current.disconnect()
      if (audioContextRef.current) audioContextRef.current.close()
    }
  }, [stream, isActive])

  return (
    <canvas
      ref={canvasRef}
      height={50}
      width={160}
      color={JoonUIColor.icon.accent}
      className={`bg-white rounded-md ${className || ""}`}
    />
  )
}

export default MailAudioModal
